import { useState } from 'react';
import { useForm } from '@mantine/form';
import {
  Grid,
} from '@mantine/core';
import { Page, ProcessHeader } from '@/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import validate from '@/helpers/validations';
import routes from '@/routes';
import { updateBorrower } from '@/data/features/proposal/proposal';
import { useSelector, useDispatch } from 'react-redux';
import {
  useSaveMutation,
  useCalculateDisposalboardMutation,
  useCalculateCycleMortgageRepaymentTableMutation,
  useGetClientByIdNumberMutation,
  useUpdateClientMutation,
} from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import BorrowerDetailsSection from '@/components/BorrowerDetailsSection';
import { toISOString } from '@/helpers/date';
import { updateProposalOffer } from '@/data/features/calculate/calculate';
import { updateMortgageCycle } from '@/data/features/mortgageCycle/mortgageCycle';
import ProcessButton from '@/components/ProcessButton';
import usersIcon from '@/assets/images/users.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './index.module.css';
import { buildUnifiedDisposalBoard } from './func';

const BorrowersDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [save] = useSaveMutation();
  const [apiUpdateClient] = useUpdateClientMutation();
  const [getClientByIdNumber] = useGetClientByIdNumberMutation();
  const [disposalboardMutationConfig] = useCalculateDisposalboardMutation();
  // eslint-disable-next-line max-len
  const [cycleMortgageRepaymentTableMutationConfig] = useCalculateCycleMortgageRepaymentTableMutation();

  const { id } = useParams();
  const borrower = useSelector((state) => state.proposal.borrower);
  const extraDetails = useSelector((state) => state.proposal.extraDetails);
  const mortgageCycleDetails = useSelector((state) => state.mortgageCycle);
  const initialBorrowers = (borrower ?? []).map((b) => ({ ...b }));
  useIdInStoreGuard();
  const { loanTypeName, isRecycling } = extraDetails;

  const msgRequired = t('globals.validations.required');
  const msgHebrew = t('globals.validations.hebrew');
  const msgPhone = t('globals.validations.phone');
  const msgEmail = t('globals.validations.email');
  const msgNotFuture = t('globals.validations.futureDate');
  const msgUid = t('globals.validations.uid');

  const formValidations = (values) => {
    const errors = {};

    if (values?.borrowers?.length === 1) {
      const b = values.borrowers[0];
      const base = 'borrowers.0';
      const set = (field, msg) => { if (msg) errors[`${base}.${field}`] = msg; };

      set('city', validate(b.city, { required: msgRequired, hebrew: msgHebrew }));
      set('address', validate(b.address, { required: msgRequired, hebrewWithDigits: t('globals.validations.hebrewWithDigits') }));
    } else {
      values.borrowers?.forEach((b, i) => {
        const base = `borrowers.${i}`;
        const set = (field, msg) => { if (msg) errors[`${base}.${field}`] = msg; };

        set('fullName', validate(b.fullName, { required: msgRequired, hebrew: msgHebrew }));
        set('city', validate(b.city, { required: msgRequired, hebrew: msgHebrew }));
        set('address', validate(b.address, { required: msgRequired, hebrewWithDigits: t('globals.validations.hebrewWithDigits') }));
        set('idNumber', validate(b.idNumber, { required: msgRequired, uid: msgUid }));
        set('phoneNumber', validate(b.phoneNumber, { required: msgRequired, phone: msgPhone }));
        set('email', validate(b.email, { required: msgRequired, email: msgEmail }));
      });
    }
    return errors;
  };

  const form = useForm({
    mode: 'uncontrolled',
    validateInputOnBlur: true,
    initialValues: {
      borrowers: initialBorrowers,
    },
    validate: formValidations,
  });
  const handleContinue = async () => {
    try {
      setLoading(true);

      const validation = form.validate();
      if (validation.hasErrors) {
        return;
      }

      const {
        borrowers,
      } = form.getValues();

      const formattedBorrowers = borrowers.map((b) => ({
        ...b,
      }));

      dispatch(updateBorrower(borrowers));

      await save({
        id,
        borrower: formattedBorrowers,
      });

      const { data: { data: completeClient } } = await getClientByIdNumber(
        { idNumber: borrower[0].idNumber },
      );
      const primaryBorrowerId = completeClient?._id;

      if (primaryBorrowerId) {
        const updateBorrowerDetails = {
          ...formattedBorrowers[0],
          id: primaryBorrowerId,
        };

        await apiUpdateClient(updateBorrowerDetails);
      }

      if (loanTypeName === 'mortgageRefinancing') {
        const path = routeWithParams(routes.newProposal.pages.find((p) => p.id === 'mortgageCycleSummary').path, { id });
        navigate(path);
        return;
      }

      const {
        propertyValuation,
        loanAmount,
        income,
        outcome,
        period,
        selectedRisk,
        loan,
        cycleMortgage,
      } = extraDetails;

      const cycleLoansWithCycleRateType = ['renovationOrExpansion', 'anyPurposeWithPropertyCollateral'];
      const objectToSendForcalculate = {
        propertyValuation,
        loan,
        loanAmount,
        income,
        outcome,
        period,
        risk: [Number(selectedRisk)],
        borrower: formattedBorrowers,
        rateType: cycleLoansWithCycleRateType.includes(loanTypeName) ? 'cycle' : 'regular',
      };

      let cycleRepaymentTable = [];
      const { currentLoans } = mortgageCycleDetails || {};

      if (isRecycling === 'yes') {
        let loansToAddToNewLoan = currentLoans.filter((l) => l.isBetterToChange === true);
        let loansToCycle = currentLoans.filter((l) => l.isBetterToChange === false);

        if (loanTypeName === 'anyPurposeWithPropertyCollateral') {
          loansToAddToNewLoan = currentLoans.filter((l) => l.isBetterToChange === true && l.loanPurpose === 'אחר');
          loansToCycle = currentLoans.filter((l) => l.isBetterToChange === false || l.loanPurpose === 'דיור');
        }
        const additionalLoanAmount = loansToAddToNewLoan.reduce(
          (sum, item) => sum + (item.totalAmount || 0),
          0,
        );

        objectToSendForcalculate.loanAmount += additionalLoanAmount;

        if (cycleMortgage.isMortgageRepaymentIncludedInPreviousLiabilities === 'yes') {
          objectToSendForcalculate.outcome -= loansToAddToNewLoan.reduce(
            (sum, item) => sum + (item.monthlyPayment || 0),
            0,
          );
        } else if (cycleMortgage.isMortgageRepaymentIncludedInPreviousLiabilities === 'no') {
          objectToSendForcalculate.outcome += loansToCycle.reduce(
            (sum, item) => sum + (item.monthlyPayment || 0),
            0,
          );
        }

        const { data: repaymentTableData } = await cycleMortgageRepaymentTableMutationConfig({
          updatedLoansForSaveAndCalculation: loansToCycle,
          isSamePeriod: true,
        }).unwrap();

        cycleRepaymentTable = repaymentTableData;
      }

      const {
        data: calculateData,
        isError: calculateErrorResponse,
      } = await getMutationResult(objectToSendForcalculate, disposalboardMutationConfig);

      const { data } = calculateData;

      const { mixes = [] } = data;

      let unifiedMixes = mixes;

      if (cycleRepaymentTable.length > 0 && mixes.length > 0) {
        const unified = buildUnifiedDisposalBoard(
          mixes,
          cycleRepaymentTable,
        );

        unifiedMixes = [unified];
      }

      if (!calculateErrorResponse) {
        dispatch(
          updateProposalOffer({
            disposalboard: unifiedMixes,
          }),
        );
      }

      navigate(routeWithParams(routes.newProposal.pages.find((p) => p.id === 'summary').path, { id }));
    } catch (error) {
      console.error('Error saving borrower data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    if (extraDetails.loanTypeName === 'mortgageRefinancing') {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'mortgageCycleAction').path, { id }));
    } else {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'offer').path, { id }));
    }
  };

  return (
    <ProcessHeader
      headerText="פרטי הלווים"
      headerColor="#EDF0F2"
      headerTextColor="#525E6F"
      showReturnButton
      subtitle="להמשך יש להשלים את כל הפרטים הנדרשים על הלווים"
      icon={usersIcon}
      onReturn={handleReturn}
    >

      {form.values.borrowers.map((_, i) => (
        <div key={`borrower-${i}`}>
          <BorrowerDetailsSection key={`borrower-${i}`} index={i} form={form} classes={classes} />
        </div>

      ))}

      <Page.Section>
        <Grid>
          <Grid.Col span={12}>
            <ProcessButton type='submit' onClick={handleContinue} loading={loading} />
          </Grid.Col>
        </Grid>
      </Page.Section>
    </ProcessHeader>
  );
};

export default BorrowersDetails;
